import { useLocation, useNavigate } from '@tanstack/react-router';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import {
  validateApiTriggeredSearch,
  validateNewslettersSearch,
  withoutDefaults,
  type ApiTriggeredSearch,
  type NewslettersSearch,
} from './url-state';
import { BROADCASTS_TAB_ROUTES } from './tabs';

export type UpdateOptions = {
  /**
   * Replace the history entry instead of adding one. List filters typed or picked in quick
   * succession (tags, object types, goals, the search) replace, so Back leaves the list rather
   * than stepping through every pick; the rest add an entry.
   */
  replace: boolean;
};

/** The one-time sends tab's search, validated, and a way to change part of it. */
export function useNewslettersTabSearch() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const current = validateNewslettersSearch(search);

  const update = (patch: Partial<NewslettersSearch>, { replace }: UpdateOptions) => {
    void navigate({
      to: BROADCASTS_TAB_ROUTES.one_time_sends,
      params: { workspaceId: String(requireWorkspaceId()) },
      search: withoutDefaults({ ...current, ...patch }),
      replace,
    });
  };

  return [current, update] as const;
}

/** The API-triggered tab's search, validated, and a way to change part of it. */
export function useApiTriggeredTabSearch() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const current = validateApiTriggeredSearch(search);

  const update = (patch: Partial<ApiTriggeredSearch>, { replace }: UpdateOptions) => {
    void navigate({
      to: BROADCASTS_TAB_ROUTES['api-triggered'],
      params: { workspaceId: String(requireWorkspaceId()) },
      search: withoutDefaults({ ...current, ...patch }),
      replace,
    });
  };

  return [current, update] as const;
}
